import axios from 'axios';
import i18n from '@/utils/i18n';
import { Notification } from 'element-ui'
import router from '../router';
import store from '../store';
import { setToken, getToken, deleteToken } from '@/utils/common';
import ENDPOINT from "@/config/endpoint";
import CONSTANTS from "@/config/constants";


function getErrorMessage(error) {
  const data = error?.response?.data;
  const detail = typeof data?.detail === 'string' ? data.detail : '';
  return data?.message || detail || i18n.t('notification.error.message');
}

// Bucket-level reads the bucket detail tabs load; object-level reads (e.g. listing one prefix) are excluded
// because a denial there doesn't mean the whole bucket is unreachable
const S3_BUCKET_READ_RESOURCES = ['files', 'objects/list', 'acl', 'versioning', 'policy', 'lifecycle', 'cors', 'static'];

function getS3BucketReadName(url) {
  const match = url.match(/\/buckets\/([^/?#]+)\/([^?#]+)/);
  // The file list calls objects/list/<prefix>, which is objects/list/ (trailing slash) at the bucket root
  if (!match || !S3_BUCKET_READ_RESOURCES.includes(match[2].replace(/\/+$/, ''))) return null;
  return decodeURIComponent(match[1]);
}

// Pages that handle a 401 themselves, so a failed token refresh must not bounce them to /login
const AUTH_PAGE_ROUTES = ['Login', 'ConfirmEmail', 'OtpVerify', 'ResetPassword', 'ForgotPassword', 'Register', 'UnlockAccount', 'IamRegister', 'SsoAuthorize'];

const request = axios.create({
  // withCredentials: true, // send cookies when cross-domain requests
  timeout: 180000, // request timeout
});

request.interceptors.request.use(
  (config) => {
    const acToken = getToken('access_token');
    if (acToken && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${acToken}`;
    }
    // Let the backend localize its error messages in the portal's language, not the browser's
    if (!config.headers['Accept-Language']) {
      config.headers['Accept-Language'] = i18n.locale;
    }
    config.s3RequestGeneration = store.getters.s3RequestGeneration;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
)


request.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    let message = '';
    if (error) {
      message = getErrorMessage(error);
      switch (error?.response?.data?.status_code) {
        case 401:
          if (error?.config?.skipAuthRefresh) break;
          if (getToken('refresh_token')) {
            try {
              const payload = {
                refresh_token: getToken('refresh_token'),
              };
              const headers = {
                Authorization: `Bearer ${getToken('access_token')}`,
              };
              const resRefresh = await axios.post(
                `${ENDPOINT.AUTH_BACKEND}/tokens/refresh`, payload, {
                headers,
              });
              setToken('access_token', resRefresh?.data?.data?.access_token);
              setToken('refresh_token', resRefresh?.data?.data?.refresh_token);
              error.config.headers['Authorization'] = `Bearer ${resRefresh?.data?.data?.access_token}`;
              return axios.request(error.config);
            } catch (error) {
              if (!AUTH_PAGE_ROUTES.includes(router.history.current.name)) router.push('/login');
            }
          } else {
            if (!AUTH_PAGE_ROUTES.includes(router.history.current.name)) router.push('/login');
          }
          break;
        case 403:
          break;
      }
    }
    const errorStatus = error?.response?.status;
    // error.config is also present on network errors/timeouts, where error.response is undefined
    const requestUrl = error?.config?.url || '';
    // Only bucket-level reads (GET) for the bucket shown on the detail page, sent since the last tab change,
    // count. A denied write (e.g. upload blocked by an SSE-required policy) or object-level read doesn't mean
    // the bucket is unreachable, and a late response from another bucket or the previous tab must not be
    // credited to the current one, so all of those keep the normal error toast
    const currentRoute = router.history.current;
    const isS3AccessDenied = errorStatus === 403 && requestUrl.includes(ENDPOINT.SIMPLE_STORAGE)
      && error?.config?.method?.toLowerCase() === 'get'
      && error?.response?.data?.extra_code === CONSTANTS.S3_ACCESS_DENIED_EXTRA_CODE
      && currentRoute.name === 'DetailS3'
      && getS3BucketReadName(requestUrl) === currentRoute.params.name
      && error?.config?.s3RequestGeneration === store.getters.s3RequestGeneration;
    if (isS3AccessDenied) {
      // The bucket detail page shows a persistent alert instead of a toast per request
      store.dispatch('updateS3AccessDeniedBucket', currentRoute.params.name);
    }
    else if (error?.response?.data?.extra_code && requestUrl?.includes('/dashboard/iam/invitations')) {
    }
    else if (errorStatus !== 401 && !requestUrl.includes('/dashboard/servers') && !requestUrl.includes('/balance') && !requestUrl.includes('/order') && router.history.current.name !== 'Login') {
      Notification(
        {
          title: i18n.t('notification.error.title'), message, customClass: 'error', type: 'error',
        },
      );
    }
    else if (router.history.current.name === 'Login') {
      if (error?.response?.data?.extra_code) {
        const extraCode = error?.response?.data?.extra_code
        if (extraCode !== 1) {
          Notification(
            {
              title: i18n.t('notification.error.title'), message, customClass: 'error', type: 'error',
            },
          );
        }
      }
    }
    return Promise.reject(error);
  },
);

export { request };